import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { APPEARANCE_LABELS } from '../../api/CourtEventApi';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BulkNoticeApi, {
  ACCEPTED_NOTICE_TYPES,
  ApprovedNotice,
  BulkNoticeBatch,
  BulkNoticeReviewRow,
  CommitBulkNoticesResult,
  ProposedEventDto,
} from '../../api/BulkNoticeApi';
import CasePicker from '../../components/CasePicker/CasePicker';

/**
 * Phase 4 — bulk pre-trial notice upload (Lloyd, Jul 22). Drop a pile of scans, check what was
 * read off them, then save.
 *
 * The review step is the feature, not a formality: a court date written onto the wrong client's
 * calendar is not caught until the date is missed. So nothing here saves as a side effect of
 * uploading — the reviewer ticks what is right, fixes what is not, and presses Save once.
 */

interface EventDraft extends ProposedEventDto {
  selected: boolean;
}

interface RowState {
  row: BulkNoticeReviewRow;
  /** The case the reviewer settled on — starts at whatever matching proposed, may be reassigned. */
  ticketGuid: string | null;
  caseLabel: string | null;
  events: EventDraft[];
  attachDocument: boolean;
  /** The reviewer's decision to send this to the court as a bond. */
  sendBondToCourt: boolean;
}

function toRowState(row: BulkNoticeReviewRow): RowState {
  return {
    row,
    ticketGuid: row.match?.ticketGuid ?? null,
    caseLabel: row.match ? `${row.match.ticketNumber} — ${row.match.clientName}` : null,
    events: row.events.map((event) => ({ ...event, selected: event.preselected })),
    attachDocument: true,
    /**
     * Starts UNTICKED even when the parser called this a bond.
     *
     * Every other suggestion on this screen pre-fills, because the cost of an unnoticed default is
     * a wrong field a person can correct later. This one sends a document to a court, and once the
     * firm turns auto-send on it does so without a second pair of eyes. Lloyd's rule was that a
     * person marks the bond and the software carries it out; a box that arrives already ticked
     * inverts that. The prompt above it does the work of drawing the eye.
     */
    sendBondToCourt: false,
  };
}

const confidenceChip = (confidence: BulkNoticeReviewRow['confidence']) => {
  if (confidence === 'high') return <Chip size="small" color="success" label="Read clearly" />;
  if (confidence === 'medium') return <Chip size="small" color="warning" label="Check this one" />;
  return <Chip size="small" color="error" label="Hard to read" />;
};

const ManagerBulkNoticesView: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [batch, setBatch] = useState<BulkNoticeBatch | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CommitBulkNoticesResult | null>(null);

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFiles(Array.from(event.target.files ?? []));
    setBatch(null);
    setRows([]);
    setResult(null);
    setError(null);
  };

  const read = useCallback(async (): Promise<void> => {
    setReading(true);
    setError(null);
    try {
      const extracted = await BulkNoticeApi.extract(files);
      setBatch(extracted);
      setRows(extracted.rows.map(toRowState));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not read the notices.');
    } finally {
      setReading(false);
    }
  }, [files]);

  const updateRow = (index: number, patch: Partial<RowState>): void => {
    setRows((current) =>
      current.map((row, position) => (position === index ? { ...row, ...patch } : row)),
    );
  };

  const updateEvent = (rowIndex: number, eventIndex: number, patch: Partial<EventDraft>): void => {
    setRows((current) =>
      current.map((row, position) =>
        position === rowIndex
          ? {
              ...row,
              events: row.events.map((event, slot) =>
                slot === eventIndex ? { ...event, ...patch } : event,
              ),
            }
          : row,
      ),
    );
  };

  /**
   * Savable means: a case is chosen, and there is something to do — a ticked date, or the file
   * itself to put on the case.
   *
   * Requiring a ticked date used to be the whole test, which silently dropped any scan without one.
   * A bond has no court date, so it vanished from the batch with no error and never reached the
   * server (Ashley, Jul 29 — "it did not upload the document because there was no date"). The
   * server was always willing: commitBulkNotices creates zero events and still files the document.
   */
  const approvals: ApprovedNotice[] = useMemo(
    () =>
      rows
        .filter(
          (row) =>
            row.ticketGuid &&
            (row.events.some((event) => event.selected) ||
              row.attachDocument ||
              row.sendBondToCourt),
        )
        .map((row) => ({
          token: row.row.token,
          fileName: row.row.fileName,
          ticketGuid: row.ticketGuid as string,
          // Sent back so the server can record it on whichever case the reviewer settled on. That is
          // what makes the next notice on this case match without them.
          causeNumber: row.row.causeNumber,
          attachDocument: row.attachDocument,
          sendBondToCourt: row.sendBondToCourt,
          events: row.events
            .filter((event) => event.selected)
            .map((event) => ({
              title: event.title,
              date: event.date,
              time: event.time,
              court: event.court,
              description: event.description,
              // Whatever the dropdown is showing, explicitly — the server keeps no record of the
              // suggestion it made, so an unsent value is simply lost and falls to the default.
              appearanceRequirement:
                event.appearanceRequirement ?? event.suggestedAppearance ?? 'NOT_REQUIRED',
            })),
        })),
    [rows],
  );

  // Anything readable that still has nowhere to go. Counts date-less scans too, now that they can be
  // filed on their own — otherwise a bond with no case picked would sit there unflagged.
  const blocked = useMemo(
    () =>
      rows.filter(
        (row) =>
          !row.row.error &&
          (row.events.length > 0 || row.attachDocument || row.sendBondToCourt) &&
          !row.ticketGuid,
      ).length,
    [rows],
  );

  const save = useCallback(async (): Promise<void> => {
    if (!batch) return;
    setSaving(true);
    setError(null);
    try {
      setResult(await BulkNoticeApi.commit(batch.batchId, approvals));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save the notices.');
    } finally {
      setSaving(false);
    }
  }, [batch, approvals]);

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Upload pre-trial notices
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Drop in as many notices as you like. Each one is read, matched to a case, and shown here
          for you to check. Nothing is saved to a calendar until you press Save.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
              Choose notices
              <input
                hidden
                multiple
                type="file"
                accept={ACCEPTED_NOTICE_TYPES}
                onChange={handleFiles}
              />
            </Button>
            <Typography variant="body2">
              {files.length === 0
                ? 'No files chosen'
                : `${files.length} file${files.length === 1 ? '' : 's'} ready`}
            </Typography>
            <Button
              variant="contained"
              disabled={files.length === 0 || reading}
              onClick={read}
              startIcon={reading ? <CircularProgress size={18} /> : undefined}
            >
              {reading ? 'Reading…' : 'Read notices'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            PDF, JPG, PNG, TIFF, BMP, WEBP or a saved email page (.html or .mhtml) · up to 25 files
            at a time.
          </Typography>
        </Paper>

        {batch && !result && (
          <>
            <Alert severity={blocked > 0 ? 'warning' : 'info'} sx={{ mb: 2 }}>
              {batch.summary.files} file(s) read · {batch.summary.matched} matched to a case ·{' '}
              {batch.summary.proposedEvents} court date(s) found
              {batch.summary.duplicates > 0 && ` · ${batch.summary.duplicates} already on the case`}
              {batch.summary.failed > 0 && ` · ${batch.summary.failed} could not be read`}
              {blocked > 0 && ` — ${blocked} still need a case picked before they can be saved.`}
            </Alert>

            {rows.map((row, rowIndex) => (
              <Paper key={`${row.row.fileName}-${rowIndex}`} sx={{ p: 3, mb: 2 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {row.row.fileName}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {row.row.citationNumber && (
                      <Chip size="small" variant="outlined" label={`Citation ${row.row.citationNumber}`} />
                    )}
                    {/* Most Texas notices print this and no citation number, so it is usually the
                        only identifier on the page. Shown so the reviewer can see what was read. */}
                    {row.row.causeNumber && (
                      <Chip size="small" variant="outlined" label={`Cause ${row.row.causeNumber}`} />
                    )}
                    {!row.row.error && confidenceChip(row.row.confidence)}
                  </Stack>
                </Stack>

                {row.row.error ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {row.row.error}
                  </Alert>
                ) : (
                  <>
                    <Alert
                      severity={
                        row.row.match?.strength === 'strong'
                          ? 'success'
                          : row.row.match
                            ? 'warning'
                            : 'error'
                      }
                      sx={{ mt: 2 }}
                    >
                      {row.row.matchNote}
                    </Alert>

                    <Box sx={{ mt: 2 }}>
                      <CasePicker
                        label="Case"
                        selectedLabel={row.caseLabel}
                        onSelect={(selection) =>
                          updateRow(rowIndex, {
                            ticketGuid: selection?.ticketGuid ?? null,
                            caseLabel: selection
                              ? `${selection.ticketNumber} — ${selection.clientName} — ${
                                  selection.courtName ?? 'no court on record'
                                }`
                              : null,
                          })
                        }
                      />
                    </Box>

                    {/*
                      Shown before the dates, deliberately. A show-cause carries a court date, so a
                      reviewer scanning for dates would tick it through as an ordinary pre-trial and
                      the thing that needed doing today would never surface.
                    */}
                    {row.row.urgentNote && (
                      <Alert
                        severity={row.row.noticeKind === 'show-cause' ? 'error' : 'warning'}
                        sx={{ mb: 2 }}
                      >
                        {row.row.urgentNote}
                      </Alert>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {row.events.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No court dates were found in this file. That is expected for a bond, a
                        licence or anything that is not a notice — pick a case above and leave
                        "attach to case" ticked to file it as a document on its own.
                      </Typography>
                    ) : (
                      row.events.map((event, eventIndex) => (
                        <Stack
                          key={`${event.title}-${event.startEpoch}-${eventIndex}`}
                          direction="row"
                          spacing={2}
                          alignItems="flex-start"
                          sx={{ mb: 2, flexWrap: 'wrap' }}
                        >
                          <Checkbox
                            checked={event.selected}
                            onChange={(e) =>
                              updateEvent(rowIndex, eventIndex, { selected: e.target.checked })
                            }
                          />
                          <TextField
                            label="What"
                            size="small"
                            value={event.title}
                            onChange={(e) =>
                              updateEvent(rowIndex, eventIndex, { title: e.target.value })
                            }
                            sx={{ minWidth: 220 }}
                          />
                          <TextField
                            label="Date"
                            size="small"
                            type="date"
                            value={event.date}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) =>
                              updateEvent(rowIndex, eventIndex, { date: e.target.value })
                            }
                          />
                          <Tooltip
                            title={
                              event.timeAssumed
                                ? 'The notice did not state a time — 9:00 AM was assumed.'
                                : ''
                            }
                          >
                            <TextField
                              label="Time"
                              size="small"
                              type="time"
                              value={event.time}
                              InputLabelProps={{ shrink: true }}
                              error={event.timeAssumed}
                              helperText={event.timeAssumed ? 'Assumed' : ' '}
                              onChange={(e) =>
                                updateEvent(rowIndex, eventIndex, { time: e.target.value })
                              }
                            />
                          </Tooltip>
                          {/* Does the client have to attend? (Lloyd, Aug 10.) The notice's own
                              words are quoted underneath when it said anything, so the reviewer is
                              confirming the document rather than trusting the parser. */}
                          <Tooltip
                            title={
                              event.appearanceQuote
                                ? `The notice said: "${event.appearanceQuote}"`
                                : 'This notice did not say either way, so the safe default applies.'
                            }
                          >
                            <TextField
                              select
                              label="Client appearance"
                              size="small"
                              value={event.appearanceRequirement ?? event.suggestedAppearance ?? 'NOT_REQUIRED'}
                              onChange={(e) =>
                                updateEvent(rowIndex, eventIndex, {
                                  appearanceRequirement: e.target
                                    .value as ProposedEventDto['suggestedAppearance'],
                                })
                              }
                              helperText={
                                event.appearanceQuote
                                  ? 'From the notice — check it'
                                  : 'Notice did not say'
                              }
                              sx={{ minWidth: 210 }}
                            >
                              {Object.entries(APPEARANCE_LABELS).map(([key, label]) => (
                                <MenuItem key={key} value={key}>
                                  {label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Tooltip>
                          {event.duplicateOf && (
                            <Chip
                              size="small"
                              color="default"
                              label={
                                event.duplicateOf.kind === 'exact'
                                  ? 'Already on this case'
                                  : 'Same day already booked'
                              }
                            />
                          )}
                        </Stack>
                      ))
                    )}

                    {row.row.rejected.map((reason, index) => (
                      <Alert severity="warning" key={index} sx={{ mt: 1 }}>
                        {reason}
                      </Alert>
                    ))}

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <Checkbox
                        checked={row.attachDocument}
                        onChange={(e) => updateRow(rowIndex, { attachDocument: e.target.checked })}
                      />
                      <Typography variant="body2">Also file this scan under the case</Typography>
                    </Stack>

                    {/*
                      The bond control. Available on every row, not only the ones the parser called
                      a bond: the parser is reading a fax-quality scan, and a bond it failed to
                      recognise is exactly the one that must still be sendable. The prompt above
                      draws the eye when it did recognise one.
                    */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Checkbox
                        checked={row.sendBondToCourt}
                        onChange={(e) =>
                          updateRow(rowIndex, { sendBondToCourt: e.target.checked })
                        }
                      />
                      <Typography variant="body2">
                        This is a bond — send it to the court
                      </Typography>
                    </Stack>

                    {row.sendBondToCourt && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        A message to the court will be prepared with this document attached. Unless
                        the firm has turned on automatic sending, it waits in the Court
                        Communication drafts for someone to review and send. If the court has no
                        usable email address on file, it goes to the internal LOR mailbox instead
                        and still needs sending by hand — you will be told which happened.
                      </Alert>
                    )}
                  </>
                )}
              </Paper>
            ))}

            <Paper sx={{ p: 3, position: 'sticky', bottom: 0 }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                <Typography variant="body2" color="text.secondary">
                  {approvals.length} file(s) ready to save
                </Typography>
                <Button
                  variant="contained"
                  disabled={approvals.length === 0 || saving}
                  onClick={save}
                  startIcon={saving ? <CircularProgress size={18} /> : undefined}
                >
                  {saving ? 'Saving…' : 'Save court dates'}
                </Button>
              </Stack>
            </Paper>
          </>
        )}

        {result && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Saved
            </Typography>
            <Alert severity={result.totals.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {result.totals.eventsCreated} court date(s) created ·{' '}
              {result.totals.documentsAttached} scan(s) filed
              {result.totals.bondsDelivered > 0 &&
                ` · ${result.totals.bondsDelivered} bond(s) prepared for the court`}
              {result.totals.duplicatesSkipped > 0 &&
                ` · ${result.totals.duplicatesSkipped} skipped as already on the case`}
              {result.totals.failed > 0 && ` · ${result.totals.failed} had problems`}
            </Alert>
            {result.results.map((row, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>{row.fileName}</strong>
                  {row.ticketNumber ? ` → ${row.ticketNumber}` : ''} · {row.eventsCreated} created
                  {row.duplicatesSkipped > 0 && `, ${row.duplicatesSkipped} skipped`}
                </Typography>
                {/*
                  'routed-internal' means the bond did NOT reach the court and somebody still has
                  to act, so it is coloured as a warning rather than sharing the success styling of
                  a real send. That distinction is the whole reason this line exists.
                */}
                {row.bondDelivery && (
                  <Alert
                    severity={
                      row.bondDelivery.outcome === 'routed-internal' ? 'warning' : 'success'
                    }
                    sx={{ mt: 0.5 }}
                  >
                    {row.bondDelivery.note}
                  </Alert>
                )}
                {row.errors.map((message, slot) => (
                  <Alert severity="warning" key={slot} sx={{ mt: 0.5 }}>
                    {message}
                  </Alert>
                ))}
              </Box>
            ))}
          </Paper>
        )}
      </Container>
    </>
  );
};

export default ManagerBulkNoticesView;
